<template>
  <div
    class="checkbox"
    :class="{ [`checkbox--marginBottom-${marginBottom}`]: true }"
    v-bind="{ 'data-analytics-checkout-checkbox': $attrs.analyticsId }"
    :data-private-info="true"
  >
    <var-style-provider :style-vars="styleVars">
      <var-checkbox
        ref="checkboxRef"
        v-model="inputValue"
        :checked-value="checkedValue"
        :unchecked-value="uncheckedValue"
        :rules="validateInput"
        :data-testid="checkboxTestId"
        :disabled="disabled"
        @change="onChange"
      >
        <div class="checkbox-label" v-html="label"></div>
      </var-checkbox>
    </var-style-provider>
  </div>
</template>

<script lang="ts" setup>
import { LDTheme } from 'ewi-ui-helpers/types';
import './Checkbox.scss';

export interface CheckboxProps {
  modelValue?: boolean | string | undefined;
  label: string;
  theme?: LDTheme;
  checkedValue?: string;
  uncheckedValue?: string;
  checkboxTestId?: string;
  validateInput?: (value: string) => string | boolean;
  required?: boolean;
  disabled?: boolean;
  marginBottom?: 'none' | 'default';
}
defineOptions({
  name: 'Checkbox',
  inheritAttrs: false,
});
const props = withDefaults(defineProps<CheckboxProps>(), {
  modelValue: false,
  theme: 'light',
  label: '',
  checkboxTestId: 'checkbox-input',
  disabled: false,
  marginBottom: 'default',
});

const checkboxRef = ref();

const lightTheme = {
  '--checkbox-checked-color': 'var(--SysLightPrimary)',
  '--checkbox-unchecked-color': 'var(--SysLightOnSurface)',
  '--checkbox-disabled-color': 'var(--SysLightOnSurface)',
  '--checkbox-error-color': 'var(--SysLightError)',
  '--checkbox-action-padding': '6px',
  '--checkbox-text-color': 'var(--SysLightOnSurface)',
  '--checkbox-icon-size': '24px',
};

const darkTheme = {
  '--checkbox-checked-color': 'var(--SysDarkPrimary)',
  '--checkbox-unchecked-color': 'var(--SysDarkOnSurface)',
  '--checkbox-disabled-color': 'var(--SysDarkOnSurface)',
  '--checkbox-error-color': 'var(--SysDarkError)',
  '--checkbox-action-padding': '6px',
  '--checkbox-text-color': 'var(--SysDarkOnSurface)',
  '--checkbox-icon-size': '24px',
};

const label = computed(() => {
  if (props.required) {
    return `${props.label} <span class="checkbox-label__required">*</span>`;
  }
  return props.label;
});

const styleVars = ref(props.theme === 'light' ? lightTheme : darkTheme);
const inputValue = ref(props.modelValue);
const emit = defineEmits(['update:modelValue', 'change']);

onMounted(() => {
  if (props.required) {
    const checkboxEl = checkboxRef.value?.$el?.querySelector('[tabindex="0"]');
    checkboxEl?.setAttribute('required', 'true');
  }
});

watch(
  () => props.modelValue,
  (value) => {
    inputValue.value = value;
  },
);

const onChange = (value: boolean) => {
  emit('update:modelValue', value);
  emit('change', value);
};

defineExpose({
  validate: () => checkboxRef.value?.validate(),
});
</script>
